import type React from "react";

import type { FeelingAtHomeWidgetSuggestionDto } from "@/api/types";
import { Modal, type ModalBaseProps } from "@/components/Modal/Modal";
import { type RatableItem, RatableSuggestionsList } from "@/components/RatableSuggestionsList/RatableSuggestionsList";
import { Button } from "@/design-system/Button";
import { Stack } from "@/design-system/Stack";
import { analyticsMutations } from "@/queries/analytics/mutations";
import { useTranslation } from "@/translations";

type HomeFahSuggestionWidgetDetailsModalProps = {
  suggestion: FeelingAtHomeWidgetSuggestionDto | undefined;
} & ModalBaseProps;

export function HomeFahSuggestionWidgetDetailsModal({
  suggestion,
  isOpened,
  onOpenChange,
}: HomeFahSuggestionWidgetDetailsModalProps): React.ReactNode {
  const { t } = useTranslation();

  const addFahSuggestionTipRatingMutation = analyticsMutations.useAddFahSuggestionTipRating();

  const handleRateTip = (tipId: string, rating: "positive" | "negative") => {
    addFahSuggestionTipRatingMutation.mutate({
      tipId,
      data: rating,
    });
  };

  const ratableItems: RatableItem[] =
    suggestion?.tips.map((tip) => ({
      id: tip.id,
      title: tip.title,
      details: tip.items.map((item) => item.item),
      rating: tip.rating,
      ratedAt: tip.ratedAt,
    })) || [];

  return (
    <Modal.Root isOpened={isOpened} onOpenChange={onOpenChange} title={suggestion?.title}>
      <Modal.Body>
        <Stack gap="sm">
          <p>{suggestion?.description}</p>
          {ratableItems.length > 0 && (
            <>
              <h4 className="text-heading-4">{t("component.analytics-widget.fah-suggestions.modal.subtitle")}</h4>
              <RatableSuggestionsList items={ratableItems} onRateItem={handleRateTip} />
            </>
          )}
        </Stack>
      </Modal.Body>
      <Modal.Actions>
        <Modal.Close>
          <Button>{t("common.action.close")}</Button>
        </Modal.Close>
      </Modal.Actions>
    </Modal.Root>
  );
}
